/**
 * playTicket — клиент play-ticket системы для запуска веб-плеера
 * (player.rublox.pro).
 *
 * Поток (см. PLAN.md «1 - Неделя 4/1 - Web-плеер на rublox.pro»):
 *   1. Юзер нажимает «Играть» на странице игры.
 *   2. rublox.pro POST'ит /api-user/api/v1/auth/play-ticket с JWT
 *      → бэк генерит uuid-ticket, кладёт в Redis на 30 сек.
 *   3. Браузер редиректит на player.rublox.pro/<id>#ticket=<ticket>.
 *   4. Плеер сам обменивает ticket на JWT через redeem-ticket.
 *
 * Локально PLAYER_BASE подменяется на http://localhost:5173 через
 * REACT_APP_PLAYER_URL в .env.development.
 */

import { API_BASE, getToken } from "./client";

/**
 * Базовый URL плеера. Логика (в порядке приоритета):
 *   1. Если сайт открыт на localhost (CRA-dev) → принудительно
 *      'http://localhost:5173', даже если REACT_APP_PLAYER_URL не задан.
 *      Страховка от граблей «забыл перезапустить npm start после
 *      создания .env.development» (она реально случалась 2026-05-25).
 *   2. REACT_APP_PLAYER_URL из .env (другой порт / dev-домен).
 *   3. Прод-дефолт https://player.rublox.pro.
 *
 * Hostname-проверка в рантайме, чтобы прод-сборка
 * (deploy в /var/www/rublox-site, реальный домен rublox.pro) шла в п.3.
 */
function _detectPlayerBase() {
  try {
    const h = typeof window !== "undefined" ? window.location.hostname : "";
    if (h === "localhost" || h === "127.0.0.1") {
      return process.env.REACT_APP_PLAYER_URL || "http://localhost:5173";
    }
  } catch (e) {
    /* SSR / нет window — игнор */
  }
  return process.env.REACT_APP_PLAYER_URL || "https://player.rublox.pro";
}
export const PLAYER_BASE = _detectPlayerBase();

// Студия — отдельный домен (studio.rublox.pro) с тем же ticket-SSO.
// На localhost можем направлять на дев-порт студии (5174 по конвенции).
function _detectStudioBase() {
  try {
    const h = typeof window !== "undefined" ? window.location.hostname : "";
    if (h === "localhost" || h === "127.0.0.1") {
      return process.env.REACT_APP_STUDIO_URL || "http://localhost:5174";
    }
  } catch (e) {
    /* SSR / нет window — игнор */
  }
  return process.env.REACT_APP_STUDIO_URL || "https://studio.rublox.pro";
}
export const STUDIO_BASE = _detectStudioBase();

/**
 * Получить одноразовый ticket для запуска плеера.
 * Возвращает строку (32 hex), либо кидает ApiError.
 */
export async function requestPlayTicket(signal) {
  const token = getToken();
  if (!token) {
    const err = new Error("not_authenticated");
    err.code = "not_authenticated";
    throw err;
  }
  /* Сначала identity v2, затем v1.
     Почему это важно: после переезда сайта на v2 токен подписан RS256,
     а старый эндпоинт `/api-user/api/v1/auth/play-ticket` его не
     принимает — отвечает 401. Запуск игры падал именно здесь: плеер
     начинал грузиться и выбрасывал на главную, потому что тикета не было.

     Оба эндпоинта отвечают одинаково — {ticket}, — поэтому разбор общий. */
  const endpoints = [
    `${API_BASE}/api/v2/identity/sso/play-ticket`,
    `${API_BASE}/api-user/api/v1/auth/play-ticket`,
  ];

  let lastErr = null;
  for (const url of endpoints) {
    let res;
    try {
      res = await fetch(url, {
        method: "POST",
        headers: { Authorization: token }, // сырой JWT, без Bearer
        signal,
      });
    } catch (e) {
      if (e?.name === "AbortError") throw e;
      lastErr = e;
      continue;
    }

    let data = null;
    try {
      data = await res.json();
    } catch {
      /* ignore */
    }

    if (res.ok && data?.ticket) return data.ticket;

    const err = new Error(data?.error || data?.detail || `ticket_failed_${res.status}`);
    err.status = res.status;
    err.code = data?.error;
    lastErr = err;
    // 401 у v2 при живом токене v2 означает реальную проблему сессии,
    // но токен мог остаться и от v1 — поэтому пробуем второй адрес.
  }
  throw lastErr || new Error("ticket_failed");
}

/**
 * Собрать URL для редиректа в плеер с одноразовым ticket.
 * Опционально передаёт выбранный скин (для localhost-разработки —
 * плеер на :5173 не имеет доступа к localStorage сайта :3000).
 */
export function buildPlayerUrl(gameId, ticket, skin) {
  const skinParam = skin ? `&skin=${encodeURIComponent(skin)}` : "";
  return `${PLAYER_BASE}/${gameId}#ticket=${ticket}${skinParam}`;
}

/**
 * Собрать URL для редиректа в студию (главная или конкретный путь).
 * subpath начинается с '/', например '/edit/42' или '/learn'. По умолчанию '/'.
 * Опционально передаёт выбранный скин в hash (для preview-плеера студии).
 */
export function buildStudioUrl(ticket, subpath = "/", skin) {
  const path = subpath.startsWith("/") ? subpath : "/" + subpath;
  const skinParam = skin ? `&skin=${encodeURIComponent(skin)}` : "";
  return `${STUDIO_BASE}${path}#ticket=${ticket}${skinParam}`;
}

/**
 * Открыть студию (главную или конкретный sub-URL).
 *
 * Если юзер залогинен — берёт ticket через /api-user/api/v1/auth/play-ticket
 * (тот же endpoint что и для плеера: ticket не привязан к домену) и редиректит
 * на STUDIO_BASE + subpath + #ticket=...
 *
 * Если юзер не залогинен — редиректит без ticket: студия сама покажет окно
 * регистрации.
 *
 * Параметры:
 *   subpath — путь внутри студии ('/', '/edit/42', '/docs', ...). По умолчанию '/'.
 *   newTab  — открыть в новой вкладке (window.open) вместо текущей. По умолчанию false
 *             (false = заменяем текущую вкладку — JWT-передача через hash работает,
 *             pop-up blocker не мешает).
 *
 * Возвращает Promise<void>. После редиректа promise может не зарезолвиться
 * (страница уже перешла), это нормально.
 */
/**
 * Открыть веб-плеер для конкретной игры (2026-05-31).
 *
 * Если юзер залогинен — берёт play-ticket и редиректит на
 * player.rublox.pro/<gameId>#ticket=<ticket>. Если не залогинен —
 * аналог openStudio: редиректит без ticket, плеер сам покажет окно
 * входа. Используется из посадочной DownloadModal (когда юзер тыкает
 * в карточку игры и уже залогинен — отправляем сразу играть).
 */
export async function openPlayer(gameId, { newTab = false } = {}) {
  let ticket = null;
  try {
    if (getToken()) {
      ticket = await requestPlayTicket();
    }
  } catch (e) {
    console.warn("[openPlayer] не смог получить ticket:", e?.message || e);
  }
  const url = ticket
    ? buildPlayerUrl(gameId, ticket)
    : `${PLAYER_BASE}/${gameId}`;
  if (newTab && typeof window !== "undefined") {
    window.open(url, "_blank", "noopener,noreferrer");
  } else if (typeof window !== "undefined") {
    window.location.href = url;
  }
}

export async function openStudio({ subpath = "/", newTab = false, skin } = {}) {
  let ticket = null;
  try {
    if (getToken()) {
      ticket = await requestPlayTicket();
    }
  } catch (e) {
    // ticket_failed — не критично, идём без авторизации, студия покажет
    // окно входа.
    console.warn("[openStudio] не смог получить ticket:", e?.message || e);
  }
  // Если skin не передан явно — читаем из localStorage сайта
  // (CabinetContext пишет туда выбранный скин). Студия 5174 не имеет
  // доступа к localStorage сайта 3000 — поэтому пробрасываем через hash.
  if (!skin && typeof localStorage !== "undefined") {
    try {
      const ls = localStorage.getItem("rublox_selected_skin");
      if (ls && typeof ls === "string") skin = ls;
    } catch (_) {}
  }
  const skinHash = skin ? `#skin=${encodeURIComponent(skin)}` : "";
  const url = ticket
    ? buildStudioUrl(ticket, subpath, skin)
    : `${STUDIO_BASE}${subpath.startsWith("/") ? subpath : "/" + subpath}${skinHash}`;

  if (newTab && typeof window !== "undefined") {
    window.open(url, "_blank", "noopener,noreferrer");
  } else if (typeof window !== "undefined") {
    window.location.href = url;
  }
}

// ─────────────────────────────────────────────────────────────────────
// Возврат после входа на внешний наш домен (studio/player) по инвайт-ссылке.
// Единый код для LoginPage (email-вход) и OAuth-callback'ов (Яндекс/Google).
// ─────────────────────────────────────────────────────────────────────

// Ключ, под которым запоминаем ?return перед уходом на OAuth-провайдера
// (state нельзя трогать — он CSRF; провайдер вернёт только code+state).
export const RETURN_AFTER_LOGIN_KEY = "rb_return_after_login";

/** Разрешаем возврат только на наши домены / localhost / внутренний путь. */
function isSafeReturnUrl(u) {
  try {
    const url = new URL(u, window.location.origin);
    const okHost =
      /(^|\.)rublox\.pro$/.test(url.hostname) ||
      url.hostname === "localhost" ||
      url.hostname === "127.0.0.1";
    return okHost || url.origin === window.location.origin;
  } catch {
    return false;
  }
}

/** Прочитать ?return из текущего URL (если валиден). */
export function getReturnUrlFromQuery() {
  try {
    const q = new URLSearchParams(window.location.search);
    /* Три имени параметра, потому что по сайту их развели три разных места:
       `?return=` — этот модуль и OAuth-колбэки;
       `?next=`   — DownloadModal («Играть» → вход → обратно к игре) и
                    страницы поддержки (/support/my);
       `?back=`   — раздел идей.
       Раньше читалось только `return`, поэтому вход по кнопке из модалки
       игры уводил в /app вместо самой игры: человек выбирал игру,
       регистрировался ради неё и терял её из виду — ломалась вся
       конверсионная цепочка лендинга.
       Проверка isSafeReturnUrl остаётся на всех трёх: это защита от
       открытого редиректа, и ослаблять её нельзя. */
    const ret = q.get("return") || q.get("next") || q.get("back");
    if (!ret || !isSafeReturnUrl(ret)) return null;
    return new URL(ret, window.location.origin).href;
  } catch {
    return null;
  }
}

/** Запомнить ?return перед уходом на OAuth (вернёмся к нему в callback). */
export function stashReturnForOAuth() {
  try {
    const ret = getReturnUrlFromQuery();
    if (ret) localStorage.setItem(RETURN_AFTER_LOGIN_KEY, ret);
    else localStorage.removeItem(RETURN_AFTER_LOGIN_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Завершить вход с учётом возврата на инвайт-ссылку.
 *  - ret берётся из аргумента, либо из ?return, либо из localStorage (OAuth).
 *  - внутренний путь rublox.pro → просто переходим.
 *  - другой наш домен (studio/player) → выпускаем ticket и уходим с #ticket.
 *  - нет return → вызываем fallback() (обычная навигация в кабинет).
 * Возвращает true, если выполнен внешний/return-редирект (дальше навигацию
 * делать НЕ нужно).
 */
export async function finishLoginWithReturn(fallback, explicitReturn) {
  let ret = explicitReturn || getReturnUrlFromQuery();
  if (!ret) {
    try {
      ret = localStorage.getItem(RETURN_AFTER_LOGIN_KEY);
      if (ret) localStorage.removeItem(RETURN_AFTER_LOGIN_KEY);
    } catch { /* ignore */ }
    if (ret && !isSafeReturnUrl(ret)) ret = null;
  }
  if (!ret) {
    if (typeof fallback === "function") fallback();
    return false;
  }
  const retUrl = new URL(ret, window.location.origin);
  if (retUrl.origin === window.location.origin) {
    window.location.replace(ret); // внутренний путь
    return true;
  }
  // другой наш домен — нужен SSO-ticket
  try {
    const ticket = await requestPlayTicket();
    const withTicket = retUrl.hash
      ? `${ret}&ticket=${ticket}`
      : `${ret}#ticket=${ticket}`;
    window.location.replace(withTicket);
  } catch {
    window.location.replace(ret); // без тикета — целевой домен попросит вход
  }
  return true;
}
